class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener(
      'click',
      this.onSummaryClick.bind(this)
    );
    this.querySelector('.search-modal__close').addEventListener(
      'click',
      this.close.bind(this)
    );
    this.querySelector('button[type="reset"]').addEventListener(
      'click',
      this.reset.bind(this)
    );

    this.summaryToggle.setAttribute('role', 'button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    event.target.closest('details').hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  onBodyClick(event) {
    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close();
  }

  open() {
    setScrollbarWidth();
    setHeaderBottomPosition();
    
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);

    /* Move to body to escape header stacking context */
    if (!this._originalParent) {
      this._originalParent = this.parentElement;
      this._originalNextSibling = this.nextSibling;

      /* Leave a placeholder so header text doesn't disappear */
      this._placeholder = document.createElement('span');
      this._placeholder.className = 'search-modal__placeholder';
      this._placeholder.innerHTML = this.summaryToggle.innerHTML;
      this._placeholder.style.cssText = this.summaryToggle.getAttribute('style') || '';
      this._placeholder.setAttribute('class', this.summaryToggle.getAttribute('class'));
      this._originalParent.insertBefore(this._placeholder, this._originalNextSibling);
    }
    document.body.appendChild(this);

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('search-modal--open');

    requestAnimationFrame(() => {
      this.detailsContainer.classList.add('menu-opening');
    });

    // Trap on the dialog itself, not on .search-modal__content — the close
    // button lives outside that inner wrapper, so trapping there let Tab walk
    // straight out into the header behind the modal (retest, C-05).
    theme.a11y.openOverlay(this.dialog, {
      opener: this.summaryToggle,
      focus: this.detailsContainer.querySelector('input:not([type="hidden"])'),
      onEscape: () => this.close(),
    });
  }

  get dialog() {
    return this.querySelector('[role="dialog"]') || this.detailsContainer;
  }

  close() {
    // Release the trap now, but DON'T restore focus yet — see restoreFocus.
    theme.a11y.closeOverlay(this.dialog, { focus: null });
    this.detailsContainer.classList.remove('menu-opening');

    /*
      Send focus back to the search toggle only once the modal has been put
      back where it belongs.

      On open, this whole <search-modal> element is moved to <body> to escape
      the header's stacking context, and on close it is moved back. The toggle
      we want to focus lives inside it, so focusing before the move meant the
      relocation immediately blurred it and focus fell to <body> (retest, C-05).
    */
    let restored = false;
    const restoreFocus = () => {
      if (restored) return;
      restored = true;
      if (this.summaryToggle && this.summaryToggle.isConnected) this.summaryToggle.focus();
    };

    const onTransitionEnd = () => {
      this.detailsContainer.removeAttribute('open');
      this.detailsContainer.removeEventListener('transitionend', onTransitionEnd);

      /* Move back to original position */
      if (this._originalParent) {
        if (this._placeholder) {
          this._placeholder.remove();
          this._placeholder = null;
        }
        if (this._originalNextSibling) {
          this._originalParent.insertBefore(this, this._originalNextSibling);
        } else {
          this._originalParent.appendChild(this);
        }
        this._originalParent = null;
        this._originalNextSibling = null;
      }

      restoreFocus();
    };
    this.detailsContainer.addEventListener('transitionend', onTransitionEnd);
    // transitionend does not fire if the transition is cancelled or reduced
    // away, and focus must not be left on <body> in that case.
    setTimeout(onTransitionEnd, 600);

    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('search-modal--open');
    document.dispatchEvent(new CustomEvent('searchmodal:close'));
  }

  reset(event) {
    event.preventDefault();
    this.querySelector('input[type="search"]').value = '';
  }
}

customElements.define('search-modal', SearchModal);
